/* eslint-disable camelcase */
import * as React from 'react';
import { Client, lookupUdprn, autocomplete } from '@ideal-postcodes/core-browser';
import { Address, AddressSuggestion } from '@ideal-postcodes/api-typings';
import { IAutoCompleteOption, IDataBinder } from '@rocketmakers/armstrong';
import { useToastOnError } from '../hooks/useApiError';
import { IPropertyAddress } from '../types';
import { commaSeperateList } from '../helpers/stringHelpers';

const api_key = APP_CONFIG.idealPostcodeApiKey;
const { idealPostcodeMock } = APP_CONFIG;

const client = new Client({ api_key });

export const useAddressUdprnLookup = () => {
  const [errors, setErrors] = React.useState();

  useToastOnError(errors);

  return async (udprn: number) => {
    if (idealPostcodeMock) {
      // eslint-disable-next-line no-param-reassign
      udprn = 0;
    }
    try {
      return lookupUdprn({ client, udprn });
    } catch (e) {
      setErrors(e);
      return undefined;
    }
  };
};

export const useAddressAutocompleteLookup = () => {
  const [errors, setErrors] = React.useState();

  useToastOnError(errors);

  return async (query: string) => {
    if (idealPostcodeMock) {
      // eslint-disable-next-line no-param-reassign
      query = 'ID1 1QD';
    }
    try {
      const results = await autocomplete.list(client, { query: { query, api_key } });

      if (!results || !results.body || !results.body.result || !results.body.result.hits) {
        return [];
      }

      return results.body.result.hits;
    } catch (e) {
      setErrors(e);
      return [];
    }
  };
};

export const mapAddressSuggestionsToOptions = (addressSuggestions: AddressSuggestion[]): IAutoCompleteOption[] => {
  if (!addressSuggestions) return [];

  return addressSuggestions.map((suggestion) => ({
    id: suggestion?.suggestion,
    name: suggestion?.suggestion,
    data: suggestion,
  }));
};

type IdealPostcodeAddressToPropertyAddress = {
  [Key in keyof Address]?: keyof IPropertyAddress;
};

export const updateFormWithIdealPostcodesAddress = <T>(dataBinder: IDataBinder<T>, address: Address) => {
  const fieldKeys: IdealPostcodeAddressToPropertyAddress = {
    line_1: 'addressLine1',
    line_2: 'addressLine2',
    line_3: 'addressLine3',
    post_town: 'city',
    county: 'county',
    country: 'country',
    postcode: 'postcode',
    administrative_county: 'administrativeCounty',
    building_name: 'buildingName',
    building_number: 'buildingNumber',
    delivery_point_suffix: 'deliveryPointSuffix',
    department_name: 'departmentName',
    dependant_locality: 'dependantLocality',
    dependant_thoroughfare: 'dependantThoroughfare',
    district: 'district',
    double_dependant_locality: 'doubleDependantLocality',
    eastings: 'eastings',
    latitude: 'latitude',
    longitude: 'longitude',
    northings: 'northings',
    organisation_name: 'organisationName',
    po_box: 'poBox',
    postal_county: 'postalCounty',
    postcode_inward: 'postcodeInward',
    postcode_outward: 'postcodeOutward',
    postcode_type: 'postcodeType',
    premise: 'premise',
    su_organisation_indicator: 'suOrganisationIndicator',
    sub_building_name: 'subBuildingName',
    thoroughfare: 'thoroughfare',
    traditional_county: 'traditionalCounty',
    udprn: 'udprn',
    umprn: 'umprn',
    uprn: 'uprn',
    ward: 'ward',
  };

  if (address) {
    Object.entries(fieldKeys).forEach(([idealPostcodeKey, propertyAddressKey]) =>
      dataBinder.setKeyValue(propertyAddressKey as keyof T, address[idealPostcodeKey]),
    );
  }
};

export const formatAddressFromFormValues = <T>(dataBinder: IDataBinder<T>): string => {
  const keys = ['addressLine1', 'addressLine2', 'addressLine3', 'city', 'postcode'];
  return commaSeperateList(...keys.map((key) => dataBinder.getValue(key)));
};
